import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import AnimatedCounter from "@/components/ui/AnimatedCounter";
import heroBg from "@/assets/hero-bg.jpg";

const Hero = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img src={heroBg} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--brand-navy))/0.95] via-[hsl(var(--brand-navy))/0.85] to-[hsl(var(--brand-navy))/0.65]" />
      </div>

      {/* Content */}
      <motion.div
        ref={ref}
        className="relative z-10 container mx-auto px-4 pt-28 sm:pt-32 pb-32 sm:pb-40"
        initial={{ opacity: 0, y: 40 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="max-w-3xl space-y-5 sm:space-y-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm"
          >
            <div className="w-2 h-2 rounded-full bg-[hsl(var(--brand-red))] animate-pulse" />
            <span className="text-white/90 text-sm font-medium tracking-wide">Cabinet de conseil depuis 2019</span>
          </motion.div>

          <motion.h1
            className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            <span className="text-[hsl(var(--brand-red))]">Études économiques</span>
            <br />
            & financières
            <span className="block text-[hsl(var(--brand-gold,40_90%_55%))] mt-2">d'excellence</span>
          </motion.h1>

          <motion.p
            className="text-base sm:text-lg text-white/80 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5, duration: 0.7 }}
          >
            A&DW Consulting accompagne les entreprises et institutions dans leurs décisions stratégiques : études de marché, analyses financières, conseil stratégique et modélisation économique.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 pt-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            <Button asChild size="lg" className="bg-[hsl(var(--brand-red))] hover:bg-[hsl(var(--brand-red))/0.9] text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-xl shadow-lg shadow-red-900/20">
              <a href="#services">
                Découvrir nos services
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-white text-[hsl(var(--brand-navy))] hover:bg-white/90 border-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-xl font-semibold">
              <a href="#projects">Nos réalisations</a>
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Stats bar at bottom */}
      <div className="absolute bottom-0 left-0 right-0 glass border-t border-white/10">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 text-center">
            <div>
              <AnimatedCounter value={30} suffix="+" className="text-2xl sm:text-3xl md:text-4xl font-bold text-[hsl(var(--brand-navy))]" />
              <div className="text-muted-foreground text-xs sm:text-sm font-medium mt-1">Projets réalisés</div>
            </div>
            <div>
              <AnimatedCounter value={10} suffix="+" className="text-2xl sm:text-3xl md:text-4xl font-bold text-[hsl(var(--brand-navy))]" />
              <div className="text-muted-foreground text-xs sm:text-sm font-medium mt-1">Effectifs permanents</div>
            </div>
            <div>
              <AnimatedCounter value={15} suffix="+" className="text-2xl sm:text-3xl md:text-4xl font-bold text-[hsl(var(--brand-red))]" />
              <div className="text-muted-foreground text-xs sm:text-sm font-medium mt-1">Partenaires</div>
            </div>
            <div>
              <AnimatedCounter value={6} className="text-2xl sm:text-3xl md:text-4xl font-bold text-[hsl(var(--brand-red))]" />
              <div className="text-muted-foreground text-xs sm:text-sm font-medium mt-1">Années d'expérience</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 text-white/50 hidden sm:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="h-6 w-6" />
      </motion.div>
    </section>
  );
};
export default Hero;
